import React, { lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import { formatShortLocation } from '@/utils/location';
import { getEntityImageUrl, makeImageFallback } from '@/lib/entityMedia';

const MapView = lazy(() => import('@/components/MapView'));

const DiscoveryMapView = ({
  filteredEntities,
  activeTab,
  tabs,
  isAdmin,
  mapFlyTo,
  setMapFlyTo,
  setSearchQuery,
}) => {
  const mappable = filteredEntities.filter((e) => e.latitude != null && e.longitude != null).length;
  const missing = filteredEntities.length - mappable;
  const activeLabel = tabs.find((t) => t.value === activeTab)?.label || 'Results';

  return (
    <section className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">
          {activeTab === 'all' ? 'All Locations' : activeLabel}
          <span className="text-muted-foreground font-normal ml-2">
            ({mappable} on map{missing > 0 ? ` · ${missing} pending` : ''})
          </span>
        </h2>
      </div>

      {missing > 0 && mappable === 0 && isAdmin && (
        <div
          className="mb-3 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm"
          data-testid="geocode-pending-banner"
        >
          <MapPin className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-foreground">
              {missing} entit{missing === 1 ? 'y has' : 'ies have'} an address but no map coordinates yet.
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              Open <strong>Admin Panel → Partners</strong> and click <strong>Geocode Missing</strong> to plot them on the map. Geocoding runs at 1 request/sec to respect Nominatim's policy and processes up to 100 per batch.
            </p>
          </div>
        </div>
      )}

      {mapFlyTo && (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm"
          data-testid="map-location-pill"
        >
          <span className="flex items-center gap-2 min-w-0">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">
              Showing area around <span className="font-medium">{mapFlyTo.label.split(',').slice(0, 2).join(',').trim()}</span>
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full h-7 px-3 text-xs shrink-0"
            onClick={() => { setMapFlyTo(null); setSearchQuery(''); }}
            data-testid="map-clear-location-btn"
          >
            Clear
          </Button>
        </div>
      )}

      <Suspense
        fallback={
          <div className="w-full h-[60vh] sm:h-[500px] rounded-2xl bg-card border border-border/50 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        }
      >
        <MapView entities={filteredEntities} flyTo={mapFlyTo} />
      </Suspense>

      {/* Quick list below map */}
      {filteredEntities.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Quick List</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntities.slice(0, 6).map((entity) => (
              <div
                key={entity.entity_id}
                className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/50 cursor-pointer transition-colors"
                onClick={() => { window.location.href = `/entity/${entity.entity_id}`; }}
              >
                <img
                  src={getEntityImageUrl(entity, { width: 400 }) || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=100'}
                  alt={entity.name}
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={makeImageFallback(entity, 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=100')}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{entity.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {formatShortLocation(entity)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default DiscoveryMapView;
